import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  EAttendanceOrder,
  EServiceOrgs,
  SERVICE_ORGS_ORDER,
} from '@/config/enums/service';
import { SSEConnectionStatus } from '@/config/enums/sse';
import useAppSelector from '@/hooks/useAppSelector';
import { useAttendanceStreamInServices } from '@/hooks/useService/useAttendanceStreamInServices';
import { useServiceLocalDB } from '@/hooks/useService/useServiceLocalDB';
import { Queue } from '@/models/Queue';
import {
  IAttendanceStreamServices,
  IOrgsPagination,
  IServiceShow,
} from '@/models/Service';
import { getServicesCache } from '@/services/api/service';
import { RootState } from '@/store';
import useDebounce from '@/utils/useDebounce';

import { useAgentPreferences } from './AgentPreferencesContext';

type ServiceProviderProps = {
  serviceList: IServiceShow[];
  botList: IServiceShow[];
  isBotDrawerVisible: boolean;
  isArchivedOpen: boolean;
  setIsArchivedOpen: (open: boolean) => void;
  serviceListOrg: {
    org: EServiceOrgs;
    show: Array<EServiceOrgs>;
  };
  setServiceListOrg: (org: {
    org: EServiceOrgs;
    show: Array<EServiceOrgs>;
  }) => void;
  serviceListData: IAttendanceStreamServices;
  getVisibleCounters: (org: EServiceOrgs) => { amount: number; new: number };
  serviceListOrder: EAttendanceOrder;
  setSearchService: (search: string) => void;
  clearSearch: () => void;
  searchResetToken: number;
  showServices: boolean;
  handleGetServices: (resetPagination: boolean) => Promise<void>;
  loadingServicePagination: boolean;
  loadingSearchServices: boolean;
  serviceListPage: IOrgsPagination;
  setServiceListOrder: (order: EAttendanceOrder) => void;
  setServiceListTags: (tags: Array<number>) => void;
  setLoadingServicePagination: (loading: boolean) => void;
  debouncedSearch: string;
  serviceListTags: Array<number>;
  loadingServices: boolean;
  hasMorePages: boolean;
  connectionStatus: SSEConnectionStatus;
  reconnect: () => void;
};

const tempServiceListDefault: IAttendanceStreamServices = {
  allIds: [],
  assumed: {
    atts: [],
    amount: 0,
    new: 0,
  },
  waiting: {
    atts: [],
    amount: 0,
    new: 0,
  },
  bot: {
    atts: [],
    amount: 0,
    new: 0,
  },
  total: {
    atts: [],
    amount: 0,
    new: 0,
  },
  group: {
    atts: [],
    amount: 0,
    new: 0,
  },
  whisper: {
    atts: [],
    amount: 0,
    new: 0,
  },
  archiveds: {
    amount: 0,
  },
  botIds: [],
  botNew: 0,
};

export const ServiceContext = createContext<ServiceProviderProps | undefined>(
  undefined,
);

export const ServiceProvider = ({ children }: { children: ReactNode }) => {
  const {
    preferences,
    handleUpsert,
    loaded: preferencesLoaded,
  } = useAgentPreferences();
  const agentId = useAppSelector(
    (state: RootState) => state.payloadAgente.UserPayloadAgent.idDynamicAgent,
  );

  const channelQueues = useAppSelector(
    (state: RootState) => state.payloadAgente.UserPayloadChannel.queues,
  );

  const isSyncingFromApi = useRef(false);
  const [hasBotAccess, setHasBotAccess] = useState(false);

  const [searchService, setSearchService] = useState<string>('');
  const [searchResetToken, setSearchResetToken] = useState(0);
  const [serviceListTags, setServiceListTags] = useState<Array<number>>([]);
  const [tempServiceList, setTempServiceList] =
    useState<IAttendanceStreamServices>(tempServiceListDefault);
  const [serviceListPage, setServiceListPage] = useState<IOrgsPagination>({
    assumed: 1,
    bot: 1,
    group: 1,
    total: 1,
    waiting: 1,
    whisper: 1,
  });
  const [serviceListOrder, setServiceListOrder] = useState<EAttendanceOrder>(
    preferences.selected_attendance_order,
  );
  const [serviceListOrg, setServiceListOrg] = useState<{
    org: EServiceOrgs;
    show: Array<EServiceOrgs>;
  }>({
    org: preferences.selected_attendance_filters[0],
    show: preferences.selected_attendance_filters,
  });
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [loadingServicePagination, setLoadingServicePagination] =
    useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingSearchServices, setLoadingSearchServices] = useState(false);

  const hasMorePages = useMemo(
    () =>
      tempServiceList[serviceListOrg.org].atts.length <
      tempServiceList[serviceListOrg.org].amount,
    [tempServiceList, serviceListOrg.org],
  );

  const getVisibleCounters = useCallback(
    (org: EServiceOrgs) => {
      const { amount, new: newAmount } = tempServiceList[org];

      if (org !== EServiceOrgs.TOTAL) return { amount, new: newAmount };

      return {
        amount: Math.max(0, amount - (tempServiceList.botIds?.length ?? 0)),
        new: Math.max(0, newAmount - (tempServiceList.botNew ?? 0)),
      };
    },
    [tempServiceList],
  );

  const debouncedSearch = useDebounce(searchService, 700);

  const clearSearch = useCallback(() => {
    setSearchService('');
    setSearchResetToken((token) => token + 1);
  }, []);

  const { connectionStatus, reconnect } = useAttendanceStreamInServices(
    setTempServiceList,
    {
      setLoadingServices,
      loadingServices,
    },
  );
  const { showServiceList, showBotList } = useServiceLocalDB(
    serviceListOrg.org,
    tempServiceList,
  );

  const handleGetServices = useCallback(
    async (resetPagination: boolean) => {
      if (!agentId) {
        setTimeout(() => {
          setLoadingServicePagination(false);
          setLoadingSearchServices(false);
        }, 200);
        return;
      }

      setLoadingServices(true);

      if (resetPagination) {
        setServiceListPage({
          ...{
            assumed: 1,
            bot: 1,
            group: 1,
            total: 1,
            waiting: 1,
            whisper: 1,
          },
        });
      } else {
        setServiceListPage((prevData) => ({
          ...{
            assumed: 1,
            bot: 1,
            group: 1,
            total: 1,
            waiting: 1,
            whisper: 1,
          },
          [serviceListOrg.org]: prevData[serviceListOrg.org] + 1,
        }));
      }

      const res = await getServicesCache(
        agentId.toString(),
        [serviceListOrg.org],
        debouncedSearch,
        serviceListTags,
        serviceListOrder,
        resetPagination ? 1 : serviceListPage[serviceListOrg.org] + 1,
        30,
      );

      setLoadingServices(false);

      if (!res.erro) {
        if (!res.data || res.data.length == 0) {
          setServiceListPage((prevData) => ({
            ...prevData,
            [serviceListOrg.org]: prevData[serviceListOrg.org] - 1,
          }));
          setTimeout(() => {
            setLoadingServicePagination(false);
            setLoadingSearchServices(false);
          }, 200);
          return;
        }

        setTimeout(() => {
          setLoadingServicePagination(false);
          setLoadingSearchServices(false);
        }, 200);
      }
    },
    [
      agentId,
      debouncedSearch,
      serviceListPage,
      serviceListOrder,
      serviceListOrg,
      serviceListTags,
    ],
  );

  useEffect(() => {
    isSyncingFromApi.current = true;
    setServiceListOrder(preferences.selected_attendance_order);
    setServiceListOrg((prev) => {
      const nextShow = preferences.selected_attendance_filters;
      const org = nextShow.includes(prev.org) ? prev.org : nextShow[0];
      return { org, show: nextShow };
    });
  }, [preferences]);

  useEffect(() => {
    if (!preferencesLoaded) return;

    const timeout = setTimeout(() => {
      handleGetServices(true);
    }, 150);

    return () => clearTimeout(timeout);
  }, [serviceListOrder, debouncedSearch, serviceListTags, preferencesLoaded]);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length > 0) {
      setLoadingSearchServices(true);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (isSyncingFromApi.current) {
      isSyncingFromApi.current = false;
      return;
    }

    const sortedFilters = [...serviceListOrg.show].sort(
      (a, b) => SERVICE_ORGS_ORDER.indexOf(a) - SERVICE_ORGS_ORDER.indexOf(b),
    );

    const filtersChanged =
      JSON.stringify(sortedFilters) !==
      JSON.stringify(preferences.selected_attendance_filters);

    const orderChanged =
      serviceListOrder !== preferences.selected_attendance_order;

    if (!filtersChanged && !orderChanged) return;

    const timeout = setTimeout(() => {
      handleUpsert({
        ...preferences,
        selected_attendance_filters: sortedFilters,
        selected_attendance_order: serviceListOrder,
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [serviceListOrder, serviceListOrg.show]);

  const canViewBot = useMemo(
    () => (channelQueues ?? []).some((queue: Queue) => queue.permitir_bot),
    [channelQueues],
  );

  useEffect(() => {
    setHasBotAccess(false);
    setIsArchivedOpen(false);
  }, [agentId]);

  useEffect(() => {
    if (!canViewBot && showBotList.length === 0) return;

    setHasBotAccess(true);
  }, [agentId, canViewBot, showBotList.length]);

  const isBotDrawerVisible =
    serviceListOrg.org === EServiceOrgs.TOTAL && hasBotAccess;

  const contextValue = useMemo(
    () => ({
      serviceList: showServiceList,
      isBotDrawerVisible,
      isArchivedOpen,
      setIsArchivedOpen,
      botList: showBotList,
      serviceListData: tempServiceList,
      getVisibleCounters,
      serviceListOrg,
      setServiceListOrg,
      serviceListOrder,
      setSearchService,
      clearSearch,
      searchResetToken,
      showServices: showServiceList.length > 0 || true,
      handleGetServices,
      loadingServicePagination,
      loadingSearchServices,
      serviceListPage,
      setServiceListOrder,
      setServiceListTags,
      setLoadingServicePagination,
      debouncedSearch,
      serviceListTags,
      loadingServices,
      hasMorePages,
      connectionStatus,
      reconnect,
    }),
    [
      showServiceList,
      showBotList,
      isBotDrawerVisible,
      isArchivedOpen,
      tempServiceList,
      getVisibleCounters,
      serviceListOrg,
      serviceListOrder,
      handleGetServices,
      loadingServicePagination,
      loadingSearchServices,
      serviceListPage,
      debouncedSearch,
      serviceListTags,
      loadingServices,
      hasMorePages,
      connectionStatus,
      reconnect,
      clearSearch,
      searchResetToken,
    ],
  );

  return (
    <ServiceContext.Provider value={contextValue}>
      {children}
    </ServiceContext.Provider>
  );
};

export function useServiceContext() {
  const context = useContext(ServiceContext);

  if (!context) {
    throw new Error(
      'useServiceContext deve ser usado dentro de um ServiceProvider',
    );
  }
  return context;
}
